import { useEffect, useReducer, useRef, useState } from 'react'
import { BackendPartner, BackendUser } from 'common/types'
import { IMentionsState } from 'common/types/mentions.types'
import { getMentionsTags } from 'common/utils/getMentionsTags'
import { useMentions } from '../../posts/hooks/useMentions'
import { getHTMLTextLength } from 'common/utils/getHTMLTextLength'
import { createMentionsState } from 'common/utils/createMentionsState'
import { useAnalytics } from 'hooks/use-analytics/useAnalytics'

const initialCommentState = createMentionsState('comment-type')

export const useCreateComment = (
  user: BackendUser,
  partner: BackendPartner,
  partnerView: boolean,
  handleComment: Function,
  postId: String,
  comments: any,
  setComments: Function,
  setCommentCount: Function,
  commentCount: number
) => {
  const {
    getResults,
    users,
    setUsers,
    setMentions,
    mentions,
    totalSuggestions,
    tags,
    setTags,
    setTagResults,
    tagResults,
    searchText
  } = useMentions()
  const { logEventAnalytics } = useAnalytics()
  const [comment, setComment] = useState<string>('')
  const commentResetMentioningRef = useRef(() => {})
  const [commentMentionsState, commentMentionStateDispatch] = useReducer<
    (state: IMentionsState, action: Partial<IMentionsState>) => IMentionsState
  >((state, action) => {
    return { ...state, ...action }
  }, initialCommentState)

  const maxCommentLength = 250

  const getUserInfo = (user: BackendUser, question: string) => {
    if (user?.onboardingQuestions && !partnerView) {
      const answer = user?.onboardingQuestions?.find(user => user?.question === question)
      return answer?.answer
    }
    return ''
  }

  const handlePostComment = async (comment: string, postAs?: string) => {
    const { mentioned, tagged, html: commentHTML } = getMentionsTags({
      mentions,
      mentionedClassIdentifier: ['.comment-val-mentioned'],
      tags,
      html: comment,
      inputElement: document.querySelector(`.${commentMentionsState?.textfieldId}`)
    })

    const feComment = await handleComment(
      user?.id,
      postId,
      commentHTML,
      postAs === 'Partner' ? partner?.id : undefined,
      mentioned,
      tagged
    )
    const partnerLog = {}
    const userLog = {}
    userLog['User ID'] = user?.id

    if (user?.partnerId) {
      partnerLog['Partner ID'] = partner?.id
      partnerLog['Company Name'] = partner?.name
      partnerLog['Account Type'] = 'Partner'
    } else {
      userLog['Account Type'] = user?.role
    }
    logEventAnalytics(
      user?.partnerId ? 'Partner' : user?.role,
      'Comment made',
      'mixpanel',
      'Comment on Post',
      { 'Post ID': postId, ...partnerLog, ...userLog },
      user?.id
    )
    setComments([
      ...[
        {
          userId: user?.id,
          id: feComment?.contentId,
          content: [{ comment: commentHTML }],
          mentions: mentioned,
          image: feComment?.partnerId ? partner?.logo : user?.image,
          key: feComment?.id,
          firstName: user?.firstName,
          lastName: user?.lastName,
          commentId: feComment?.id,
          connectionTitle: getUserInfo(user, 'Headline'),
          partnerId: feComment?.partnerId,
          companyName: postAs === 'Partner' ? partner?.name : null
        }
      ],
      ...comments
    ])
    setCommentCount(Number(commentCount) + 1)
    setComment('')
    commentMentionStateDispatch(initialCommentState)
  }

  const notPassingMaxLength = commentMentionsState.textfieldCharacterLength <= maxCommentLength
  const notEmpty = commentMentionsState.textfieldCharacterLength > 0

  const canPost = notEmpty && notPassingMaxLength

  useEffect(() => {
    const characterLength = getHTMLTextLength(commentMentionsState.textfieldValue)

    commentMentionStateDispatch({ textfieldCharacterLength: characterLength })
  }, [commentMentionsState.textfieldValue])

  return {
    getResults,
    users,
    setUsers,
    setMentions,
    mentions,
    totalSuggestions,
    tags,
    setTags,
    setTagResults,
    tagResults,
    searchText,
    handlePostComment,
    comment,
    commentMentionsState,
    commentMentionStateDispatch,
    maxCommentLength,
    commentResetMentioningRef,
    canPost
  } as const
}
